import React, { useCallback, useEffect, useState } from 'react';
import { useStringsContext } from '../../../../contexts/strings';
import Stack from '@mui/material/Stack';
import { AppConfigFeatureFlagsComment } from '../../../../types/app-config';
import Typography from '@mui/material/Typography';
import Switch from '@mui/material/Switch';
import { doc, updateDoc } from 'firebase/firestore';
import firebaseApp from '../../../../firebase-app';
import { getAppConfigPath } from '../../../../utils/paths';
import { useDocumentDataOnce } from 'react-firebase-hooks/firestore';
import { useSnackbar } from 'notistack';
import SocialCommentWarningModal from './social-comment-warning-modal';

type FeatureFlagsComment = keyof AppConfigFeatureFlagsComment;

const featureFlagsDisableComments: Array<FeatureFlagsComment> = [
  'disableComments',
  'disableCommentsNews',
  'disableCommentsStories',
  'disableCommentsArticles',
];

type CommentSettings = Record<FeatureFlagsComment, boolean>;

const defaultCommentSettings: CommentSettings = {
  disableComments: false,
  disableCommentsNews: false,
  disableCommentsStories: false,
  disableCommentsArticles: false,
};

interface Props {
  selectedAbbrv: string | null;
}

const SocialCommentSettings = ({ selectedAbbrv }: Props) => {
  const { strings } = useStringsContext();
  const { enqueueSnackbar } = useSnackbar();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [commentSettings, setCommentSetting] = useState<CommentSettings>(defaultCommentSettings);

  const [appConfig, appConfigLoading, appConfigError] = useDocumentDataOnce(
    selectedAbbrv ? doc(firebaseApp.db, getAppConfigPath(selectedAbbrv)) : undefined
  );

  useEffect(() => {
    if (appConfig && !appConfigLoading && !appConfigError) {
      const updatedCommentSettings = { ...defaultCommentSettings };
      featureFlagsDisableComments.forEach((featureFlag) => {
        updatedCommentSettings[featureFlag] = !!appConfig?.featureFlags?.[featureFlag];
      });
      setCommentSetting(updatedCommentSettings);
    }
  }, [appConfig, appConfigError, appConfigLoading]);

  const handleSubmit = useCallback(
    async (featureFlag: FeatureFlagsComment) => {
      if (!appConfig) {
        return;
      }

      setShowWarningModal(false);
      setIsSubmitting(true);

      const updatedCommentSettings = {
        ...commentSettings,
        [featureFlag]: !commentSettings[featureFlag],
      };

      try {
        const docRef = doc(firebaseApp.db, getAppConfigPath(selectedAbbrv!));
        await updateDoc(docRef, {
          featureFlags: {
            ...(appConfig.featureFlags ? appConfig.featureFlags : {}),
            ...updatedCommentSettings,
          },
        });

        setCommentSetting(updatedCommentSettings);
        setIsSubmitting(false);

        enqueueSnackbar(strings.messages.saveSuccess, {
          variant: 'success',
        });
      } catch (error) {
        console.error(error);

        enqueueSnackbar(strings.messages.saveError, {
          variant: 'error',
        });
      }
    },
    [appConfig, commentSettings, enqueueSnackbar, selectedAbbrv, strings]
  );

  const handleCommentToggle = useCallback(
    (featureFlag: FeatureFlagsComment) => {
      if (featureFlag === 'disableComments') {
        setShowWarningModal(true);
      } else {
        handleSubmit(featureFlag);
      }
    },
    [handleSubmit]
  );

  return (
    <>
      <Stack spacing={2} sx={{ marginBottom: 4 }}>
        <table>
          {featureFlagsDisableComments.map((featureFlag) => (
            <tr>
              <td>
                <Typography key={featureFlag}>
                  {(strings.social.commentSettingsLabels as any)[featureFlag]}
                </Typography>
              </td>
              <td>
                <Switch
                  size="small"
                  disabled={
                    (commentSettings['disableComments'] && featureFlag !== 'disableComments') ||
                    isSubmitting
                  }
                  checked={!commentSettings[featureFlag]}
                  onChange={() => handleCommentToggle(featureFlag)}
                />
              </td>
            </tr>
          ))}
        </table>
      </Stack>
      {showWarningModal && (
        <SocialCommentWarningModal
          isOpen={showWarningModal}
          onClose={() => setShowWarningModal(false)}
          onAccept={() => handleSubmit('disableComments')}
        />
      )}
    </>
  );
};

export default SocialCommentSettings;
